// GENERATED from shared/api-types.ts — do not edit.
// Run `node scripts/sync-shared-types.mjs` from the repo root to regenerate.

/**
 * Canonical API types shared verbatim across web, widget, and mobile.
 *
 * SOURCE OF TRUTH — edit here, then run:
 *     node scripts/sync-shared-types.mjs
 * to regenerate `src/lib/sharedApiTypes.ts` in each app. The generated copies
 * carry a do-not-edit banner; never hand-edit them.
 *
 * Only types that are *field-identical* across all three clients belong here.
 */

export type SubscriptionStatus = {
  status: string;
  access_allowed?: boolean;
  plan_tier?: string;
  plan_label?: string;
  trial_ends_at?: string | null;
  approved?: boolean;
  is_admin?: boolean;
  access_role?: string;
  allowed_modes?: string[];
  usage_count?: number;
  usage_limit?: number | null;
  richtlijn_analyse_enabled?: boolean;
  organization_linked?: boolean;
  clinical_codes_available?: boolean;
  // Org-beleid: minuten na verwerking waarna de audio gewist wordt; null =
  // audio volgt de bewaartermijn van de opname (migratie 124).
  audio_retention_minutes?: number | null;
};

export type RecordingSummary = {
  id: string;
  display_name: string;
  recorded_at: string;
  expires_at: string;
  duration_seconds: number | null;
  size_bytes: number | null;
  mime_type: string;
  source: "live" | "ambient";
  status: string;
  transcription_ref: string | null;
  has_transcript: boolean;
  transcript_mode?: string | null;
  transcript_content_type?: string | null;
  transcript_updated_at: string | null;
  // Ambient-summary fields: the backend's _row_to_response() emits both for
  // every recording (has_ambient_summary is always a bool); optional here so
  // clients that don't read them stay unaffected.
  has_ambient_summary?: boolean;
  ambient_summary_updated_at?: string | null;
  // Section-structured verslag persisted on the row (migration 056).
  has_verslag?: boolean;
  // Decrypted patient ref for the owner; "" when none was coupled.
  patient_ref?: string;
  deleted_at?: string | null;
  // Org-beleid (migratie 124): audio_purge_at is het moment waarop de audio
  // aan de beurt is, audio_deleted_at het moment waarop het S3-object echt
  // gewist is. De rij zelf blijft in beide gevallen bestaan.
  audio_purge_at?: string | null;
  audio_deleted_at?: string | null;
};

export type RecordingTranscript = {
  text: string;
  mode: string | null;
  content_type: string | null;
  updated_at: string | null;
};

export const TRANSLATE_ARCHIVE_TYPE = "carmen.translate.v1";

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function archiveString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function serializeTranslateArchive(
  segments: readonly unknown[],
  summary: unknown,
  displayText = "",
): string {
  return JSON.stringify({
    type: TRANSLATE_ARCHIVE_TYPE,
    segments,
    summary,
    display_text: displayText.trim(),
  });
}

/** Keep a structured Vertaal archive readable in clients that do not render
 * its individual sections. Ordinary transcripts pass through unchanged. */
export function displayRecordingTranscriptText(
  text: string,
  contentType?: string | null,
): string {
  if (contentType && contentType !== "translator") return text;
  try {
    const raw: unknown = JSON.parse(text);
    if (!isJsonObject(raw) || raw.type !== TRANSLATE_ARCHIVE_TYPE) return text;

    const sections: string[] = [];
    if (isJsonObject(raw.summary)) {
      const dutchText = archiveString(raw.summary.dutch_text);
      const patientText = archiveString(raw.summary.patient_text);
      if (dutchText) sections.push(`Samenvatting\n${dutchText}`);
      if (patientText) sections.push(`Patiëntbrief\n${patientText}`);
    }

    if (Array.isArray(raw.segments)) {
      const transcript = raw.segments
        .filter(isJsonObject)
        .map((segment) => {
          const speaker = segment.speaker === "patient" ? "Patiënt" : "Arts";
          const sourceName = archiveString(segment.source_language_name) || "Bron";
          const targetName = archiveString(segment.target_language_name) || "Vertaling";
          const source = archiveString(segment.source_text);
          const translated = archiveString(segment.translated_text);
          const lines = source ? [`${speaker} · ${sourceName}: ${source}`] : [];
          if (translated) lines.push(`${speaker} · ${targetName}: ${translated}`);
          return lines.join("\n");
        })
        .filter(Boolean)
        .join("\n\n");
      if (transcript) sections.push(`Transcript\n${transcript}`);
    }

    if (sections.length) return sections.join("\n\n");
    return archiveString(raw.display_text ?? raw.displayText);
  } catch {
    return text;
  }
}

export type Announcement = {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
  expires_at: string;
};
